// src/features/participants/page/Customer

import DynamicTable from '@/common/components/DynamicTable/DynamicTable';
import { Button, Spin } from 'antd'; // <-- Add Spin here
import type { ColumnsType } from 'antd/es/table';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import type { CustomerType, PolicyHolderTypeColumns } from '../../types/customersTypes';
import CustomerCard from './CustomerCard';
import { useQuery } from '@tanstack/react-query';
import { post } from '../../../../common/services/httpClient';
import { useRouter } from "@tanstack/react-router";
import StatusTag from '../../../../common/components/Status';
import { ACTIVE_INACTIVE_OPTIONS } from '../../../../common/staticData/options';
import dayjs from 'dayjs';
// ----------------- Types -----------------

const Customer = () => {
    const router = useRouter();
    const { data, isLoading } = useQuery({
        queryKey: [QUERY_KEYS.CUSTOMERS.CUSTOMER],
        queryFn: async () => {
            const res = await post<{ data: CustomerType[]; userType: string }>(
                API_ENDPOINTS.CUSTOMERS.SEARCH_CUSTOMER,
                {}
            );
            return res;
        },
    });

    // Case 0: Loading state
    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <Spin tip="Loading..." size="large" />
            </div>
        );
    }

    // Case 1: if userType = Customer then show card 
    if (data?.userType === "Customer") {
        return <CustomerCard Customer={data.data[0]} />;
    }

    // Case 2: otherwise default table will render 
    // Define columns for the Customer table
    const CustomerColumns: ColumnsType<CustomerType> = [
        {
            title: 'Name',
            key: 'name',
            render: (_, record) => (
                <Button
                    type="link"
                    onClick={() => {
                        router.navigate({
                            to: '/customer/detail', // Corrected path
                            state: { record: record } as Record<string, unknown>,
                        });
                    }}
                >
                    {record.name}
                </Button>
            ),
        },
        //{ title: "Name", dataIndex: "name", key: "name", sorter: true },
        { title: "Phone", dataIndex: "phoneNo", key: "phoneNo" },
        { title: "Mobile", dataIndex: "mobileNo", key: "mobileNo" },
        { title: "Email", dataIndex: "email", key: "email" },
        //{ title: "Website", dataIndex: "webSite", key: "webSite" },
        { title: "Country", dataIndex: "country", key: "country" },
        { title: "City ", dataIndex: "city", key: "city" },
        //{ title: "Area", dataIndex: "area", key: "area" },
        //{ title: "Street No", dataIndex: "streetNo", key: "streetNo" },
        //{ title: "Address", dataIndex: "address", key: "address" },
        //{ title: "Note", dataIndex: "note", key: "note" },
        {
            title: "Active",
            dataIndex: "isActive",
            key: "isActive",
            render: (isActive: boolean) => (
                <StatusTag status={isActive ? "Yes" : "No"} />
            ),
        },
    ];

    // ----------------- Child Columns -----------------
    const childColumns: ColumnsType<PolicyHolderTypeColumns> = [
        { title: "Card No", dataIndex: "cardNumber", key: "cardNumber" },
        { title: "Name", dataIndex: "name", key: "name" },
        { title: "Company Name", dataIndex: "companyName", key: "companyName" },
        { title: "Plan", dataIndex: "planName", key: "planName" },
        { title: "Group", dataIndex: "groupName", key: "groupName" },
        {
            title: "Effective Date",
            dataIndex: "effectiveDate",
            key: "effectiveDate",
            render: (date: string | null) =>
                date ? dayjs(date).format('DD-MM-YYYY') : "-",
        },
        {
            title: "Expiry Date",
            dataIndex: "expiryDate",
            key: "expiryDate",
            render: (date: string | null) =>
                date ? dayjs(date).format('DD-MM-YYYY') : "-",
        },
        { title: "Mobile", dataIndex: "mobileNo", key: "mobileNo" },
        {
            title: "DOB",
            dataIndex: "dob",
            key: "dob",
            render: (date: string | null) =>
                date ? dayjs(date).format('DD-MM-YYYY') : "-",
        },
        { title: "City", dataIndex: "city", key: "city" },
        //{ title: "Address", dataIndex: "address", key: "address" },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <StatusTag status={status} />
            ),
        },
    ];

    // ---------- Expandable Row ----------
    const expandedRowRender = (record: CustomerType) => {
        return (
            <div style={{ margin: "0px 25px" }}>
                <DynamicTable<PolicyHolderTypeColumns>
                    key={record.id}
                    tableTitle={`Policy Holder(s): ${record.name}`}
                    dataApiUrl={API_ENDPOINTS.CUSTOMERS.SEARCH_POLICY_HOLDERS}
                    baseQueryKey={["CustomerDetailList", String(record.id)]}
                    columns={childColumns}
                    showActions={false}
                    addNewPermission="false"
                    extraParameters={{ CustomerId: record.id }}
                    filterConfigs={[
                        {
                            field: "searchBy", placeholder: "Select option", type: 'select', showSearch: true,

                            options: [
                                { label: 'Name', value: 'Name' },
                                { label: 'Card Number', value: 'CardNumber' },
                                { label: 'Policy Code', value: 'PolicyCode' }
                            ]

                        },
                        { field: "searchValue", placeholder: "Search by policy holder name", type: 'input' },
                        {
                            field: 'companyId',
                            type: 'select',
                            placeholder: "Select company",
                            showSearch: true,
                            dynamicOptions: {
                                url: "/Lookup/GetDropdownData",
                                method: "POST",
                                body: { "dropdowns": ["company"] },
                                transformData: (apiResponse: unknown) => {
                                    const response = apiResponse as { company: { Name: string; Id: number }[] };
                                    return response.company.map((item) => ({ label: item.Name, value: item.Id }))
                                }
                            },
                        },
                        {
                            field: "Print",
                            type: "export",
                            exportOptions: {
                                url: API_ENDPOINTS.CUSTOMERS.EXPORT_POLICYHOLDER,
                                fileName: "PolicyHolder",
                                fileType: "excel", // "csv" 
                                defaultPayload: { CustomerId: record.id },
                                columns: [
                                    { header: "Card Number", key: "cardNumber" },
                                    { header: "Name", key: "name" },
                                    { header: "Company Name", key: "companyName" },
                                    { header: "Plan", key: "planName" },
                                    { header: "Group", key: "groupName" },
                                    {
                                        header: "Effective Date",
                                        key: "effectiveDate",
                                        transform: (date: string) => date ? dayjs(date).format('DD-MM-YYYY') : "-"
                                    },
                                    {
                                        header: "Expiry Date",
                                        key: "expiryDate",
                                        transform: (date: string) => date ? dayjs(date).format('DD-MM-YYYY') : "-"
                                    },
                                    { header: "Mobile", key: "mobileNo" },
                                    {
                                        header: "DOB",
                                        key: "dob",
                                        transform: (date: string) => date ? dayjs(date).format('DD-MM-YYYY') : "-",
                                    },
                                    { header: "City", key: "city" },
                                    //{ header: "Address", key: "address" },
                                    { header: "Status", key: "status" }
                                ]
                            }
                        }
                    ]}
                />
            </div>
        );
    };

    return (
        <div>
            <DynamicTable<CustomerType> // Ensure generic type matches the data
                dataApiUrl={API_ENDPOINTS.CUSTOMERS.SEARCH_CUSTOMER} // Your API endpoint for fetching menus
                baseQueryKey={QUERY_KEYS.CUSTOMERS.CUSTOMER} // Unique query key for menus
                columns={CustomerColumns}
                tableTitle={"Customers"}
                deleteBaseUrl={API_ENDPOINTS.CUSTOMERS.DELETE_CUSTOMER}
                deletePayloadField={"name"}
                expandable={{
                    expandedRowRender,
                    rowExpandable: (record) => record.id !== undefined,
                }}
                // Pass the fields you want the search bar to filter
                filterConfigs={[
                    { field: "name",type:'input', placeholder: "Search by Customer name" },
                    {
                        field: "status", placeholder: "Select Status", type: 'select', showSearch: true,
                        options: ACTIVE_INACTIVE_OPTIONS
                    },                   
                    {
                        field: "Print",
                        type: "export",
                        exportOptions: {
                            url: API_ENDPOINTS.CUSTOMERS.EXPORT_CUSTOMER,
                            fileName: "Customers",
                            fileType: "excel", // "csv" 
                            defaultPayload: { },
                            columns: [
                                { header: "Name", key: "name" },
                                { header: "Phone", key: "phoneNo" },
                                { header: "Mobile", key: "mobileNo" },
                                { header: "Email", key: "email" },
                                { header: "Country", key: "country" },
                                { header: "City", key: "city" },
                                { header: "Status", key: "isActive", transform: (val: boolean) => val === true ? "Active" : "Inactive" }
                            ]
                        }
                    }

                ]}
            />
        </div>
    );
};

export default Customer;